// Copying, and telling the truth about it.
//
// `navigator.clipboard.writeText` is a promise that REJECTS when the browser says
// no: a webview without the permission, a page that is not secure, a document that
// lost focus, a Safari that wants a fresh tap. Callers wrote
// `try { void navigator.clipboard.writeText(t) } catch {}` and then showed "copied"
// regardless, which only guards against a synchronous throw; the rejection went
// unhandled and the pill said "copied ✓" over an empty clipboard. So a copy is an
// awaited yes or no here, and "copied" is only ever shown on a yes.

/** What a person is told when the clipboard said no. It points at the thing they
 *  can still do: the text is on screen, and (see selectNodeText) already selected. */
export const COPY_FAILED_LINE = "couldn't copy. please select it and copy by hand."

/** A clipboard that never answers (a permission prompt nobody sees) is a no. */
const COPY_DEADLINE_MS = 4_000

export async function copyText(
  text: string,
  clip: Pick<Clipboard, 'writeText'> | undefined = typeof navigator !== 'undefined' ? navigator.clipboard : undefined,
): Promise<boolean> {
  if (!clip || typeof clip.writeText !== 'function') return false
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      clip.writeText(text),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('the clipboard did not answer')), COPY_DEADLINE_MS)
      }),
    ])
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

/** Selects everything inside `el`, so that when a copy failed the person's own
 *  copy shortcut finishes the job. Never throws. */
export function selectNodeText(el: Element | null | undefined): void {
  try {
    if (!el || typeof window === 'undefined') return
    const sel = window.getSelection()
    if (!sel) return
    const range = document.createRange()
    range.selectNodeContents(el)
    sel.removeAllRanges()
    sel.addRange(range)
  } catch {
    /* a browser that refuses selection: the line above still says what to do */
  }
}
